import { useState, useEffect, useRef } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { getApi } from '../../providers/AuthContext';
import ChildForm from '../../components/Children/Form';
import PageWrapper from '../../components/PageWrapper';
import { AlertCircle, ArrowLeft } from 'lucide-react';

const FormSkeleton = () => (
  <div className="form-container">
    <div className="form-wrapper space-y-6">
      <div className="flex flex-col items-center">
        <div className="skeleton w-[200px] h-[200px] sm:w-[300px] sm:h-[300px] rounded-full" />
        <div className="skeleton h-10 w-36 mt-4 rounded-xl" />
      </div>
      <div>
        <div className="skeleton h-4 w-24 mb-3" />
        <div className="skeleton h-12 w-full rounded-xl" />
      </div>
      <div>
        <div className="skeleton h-4 w-16 mb-3" />
        <div className="skeleton h-24 w-full rounded-xl" />
      </div>
      <div className="flex gap-2">
        <div className="skeleton h-10 w-20 rounded-xl" />
        <div className="skeleton h-10 w-24 rounded-xl" />
      </div>
    </div>
  </div>
);

const Page: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const api = getApi();
  const loadedId = useRef<string | null>(null);

  useEffect(() => {
    if (loadedId.current === id) return;
    loadedId.current = id;

    const loadChild = async () => {
      try {
        setError(null);
        setLoading(true);
        const result = await api.get(`/api/v1/children/${id}`);
        setChild(result.child);
      } catch (err) {
        setError('Failed to load child.');
      } finally {
        setLoading(false);
      }
    };

    loadChild();
  }, [id]);

  return (
    <PageWrapper pageTitle="Update Child">
      {loading ? (
        <FormSkeleton />
      ) : error ? (
        <div className="p-4 lg:p-6 max-w-lg mx-auto">
          <div className="p-4 bg-destructive/10 dark:bg-destructive/20 text-destructive border border-destructive/20 rounded-xl flex items-center gap-3">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <span className="flex-1 text-sm font-medium">{error}</span>
            <button
              onClick={() => history.push('/children')}
              className="text-sm font-medium underline text-destructive hover:text-destructive/80 flex items-center gap-1"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              Go Back
            </button>
          </div>
        </div>
      ) : (
        <ChildForm child={child} />
      )}
    </PageWrapper>
  )
}

export default Page;
